/*!
 * capture.js — capture(): the window, with Oscilla's own sound, to a file
 * © 2026 Rob Canning — GPLv3
 *
 *   capture()                      start (or, recording, stop and save)
 *   capture(stop)                  stop and save
 *   capture(size:720, fps:60, format:webm, wav:1, path:recordings/takes)
 *   capture(sound)                 the sound alone, no picture: a WAV
 *   capture(sound, flac:1, bits:24)
 *
 *   size:   the window's own size (default); 720: that height, the width
 *           kept to the window's shape; [1920, 1080]: exactly that
 *   fps:    30 (default)
 *   format: mp4 (default; H.264 + AAC where the browser can, else webm),
 *           webm (VP9 / VP8 + Opus)
 *   wav:1   also the sound alone, lossless (a WAV of the same mix)
 *   flac:1  ... as FLAC (up to 8 channels; more, and it is a WAV)
 *   bits:   16 (default) or 24, for the WAV or FLAC
 *   path:   a folder in the project, recordings/ by default
 *
 * The WAV or FLAC has as many channels as the speakers (speakers.js):
 * channel n is output n. The video's sound is two channels, a fold-down.
 *
 * The picture is the browser's capture of this tab (in the desktop app,
 * without asking: main.cjs answers the request); the sound is taken from
 * Oscilla's audio engine -- the mix as it is made, no microphone, no system
 * sounds. Both go to the project folder as they are recorded, a piece a
 * second, so a long take never sits in memory and a crash keeps what was
 * recorded. Files are named by the time they started: capture-2026-10-12_21-04-33.mp4.
 */

import { masterMix } from "../cues/audio/audioShared.js";
import { outputWidth } from "../cues/audio/speakers.js";
import { getProjectName } from "../interaction/shared.js";
import { interleavePCM, wavHeader, wavSizes, flacHeader, flacStreamInfo, flacEncoder, FLAC_MAX_CHANNELS } from "./soundFiles.js";

let rec = null;      // the recording under way

const log = (msg, kind = "info") => {
  window.oscillaLiveLog?.(msg, kind);
  (kind === "err" ? console.warn : console.log)("[capture]", msg);
};

const stamp = () => {
  const d = new Date(), p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
};

/** A folder inside the project: words, digits, - _ and spaces, no dots. */
function cleanPath(p) {
  const parts = String(p ?? "recordings").split("/").map(s => s.trim()).filter(Boolean);
  if (!parts.length || parts.some(s => !/^[\w -]+$/.test(s))) return null;
  return parts.join("/");
}

/** Send a piece of a file to the project (op: append, or patch at offset). */
async function send(r, name, data, op = "append", offset = 0) {
  const q = new URLSearchParams({ path: r.path, name, op, offset: String(offset) });
  const res = await fetch(`/api/capture/${encodeURIComponent(r.project)}?${q}`, {
    method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: data,
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || res.statusText);
}

function pickMime(format) {
  const MR = window.MediaRecorder;
  // H.264 + AAC where the browser has AAC (macOS, Windows), else H.264 with
  // Opus (Chrome on Linux) -- still an .mp4 that players and ffmpeg read
  const mp4 = ["video/mp4;codecs=avc1.640028,mp4a.40.2", "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
               "video/mp4;codecs=avc1,mp4a.40.2", "video/mp4;codecs=avc1.640028,opus", "video/mp4;codecs=avc1,opus", "video/mp4"];
  const webm = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  const list = format === "webm" ? webm : [...mp4, ...webm];
  const mime = list.find(m => MR?.isTypeSupported?.(m));
  return mime ? { mime, ext: mime.startsWith("video/mp4") ? "mp4" : "webm" } : null;
}

// ---- the sound file: the same mix, every channel, sent as it comes --------

const WORKLET = `
class OscillaCaptureTap extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0];
    if (ch && ch.length) this.port.postMessage(ch.map(c => c.slice(0)));
    return true;
  }
}
registerProcessor("oscilla-capture-tap", OscillaCaptureTap);`;

/** kind: "wav" or "flac"; as many channels as the speakers have now. */
async function startSound(r, ctx, kind, bits) {
  const url = URL.createObjectURL(new Blob([WORKLET], { type: "text/javascript" }));
  await ctx.audioWorklet.addModule(url);
  URL.revokeObjectURL(url);
  const channels = outputWidth();
  if (kind === "flac" && channels > FLAC_MAX_CHANNELS) {
    log(`flac: at most ${FLAC_MAX_CHANNELS} channels, these speakers have ${channels} -- a WAV instead`, "info");
    kind = "wav";
  }
  // the speakers chosen later in the take: folded to these channels
  const node = new AudioWorkletNode(ctx, "oscilla-capture-tap", {
    numberOfInputs: 1, numberOfOutputs: 0,
    channelCount: channels, channelCountMode: "explicit", channelInterpretation: channels > 2 ? "discrete" : "speakers",
  });
  masterMix.connect(node);
  const w = { node, kind, bits, channels, rate: ctx.sampleRate, name: `${r.base}.${kind}`, bytes: 0, pending: [], pendingLen: 0, chain: Promise.resolve() };
  w.enc = kind === "flac" ? flacEncoder({ rate: w.rate, channels, bits }) : null;
  await send(r, w.name, kind === "flac" ? flacHeader(w.rate, channels, bits) : wavHeader(w.rate, channels, bits), "append");   // sizes patched at the end
  node.port.onmessage = (e) => {
    const chans = e.data;
    for (const out of w.enc ? w.enc.push(chans) : [interleavePCM(chans, bits)]) { w.pending.push(out); w.pendingLen += out.byteLength; }
    if (w.pendingLen >= w.rate * channels) flushSound(r, w);   // a second or so
  };
  return w;
}

function flushSound(r, w) {
  if (!w.pendingLen) return w.chain;
  const blob = new Blob(w.pending);
  w.bytes += w.pendingLen;
  w.pending = []; w.pendingLen = 0;
  w.chain = w.chain.then(() => send(r, w.name, blob)).catch(err => log(`${w.kind}: ${err.message}`, "err"));
  return w.chain;
}

/** The take's end: the last of it, then its sizes over the header. */
async function endSound(r, w) {
  try { masterMix.disconnect(w.node); } catch { /* gone */ }
  w.node.port.onmessage = null;
  for (const out of w.enc?.end() || []) { w.pending.push(out); w.pendingLen += out.byteLength; }
  await flushSound(r, w);
  await w.chain;
  try {
    if (w.kind === "flac") await send(r, w.name, flacStreamInfo(w.enc.info()), "patch", 8);
    else {
      const { pad, patches } = wavSizes(w.rate, w.channels, w.bits, w.bytes);
      if (pad) await send(r, w.name, new Uint8Array(1));
      for (const p of patches) await send(r, w.name, p.bytes, "patch", p.offset);
    }
  } catch (err) { log(`${w.kind}: ${err.message}`, "err"); }
}

// ---- the indicator ----------------------------------------------------------

// The page is what is recorded, so a mark on it would be in the picture:
// it shows for two seconds as the recording starts, then goes; the console's
// header carries the time while it runs.
function showIndicator(r) {
  const el = document.createElement("div");
  el.className = "oscilla-capture-rec";
  el.textContent = `● rec · ${r.label}`;
  document.body.appendChild(el);
  r.indicator = el;
  setTimeout(() => { el.remove(); if (r.indicator === el) r.indicator = null; }, 2000);
  const title = document.querySelector(".livecode-header-title");
  const plain = title?.textContent;
  const t0 = performance.now();
  const tick = () => {
    const s = Math.floor((performance.now() - t0) / 1000);
    if (title) title.textContent = `${plain} ● ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };
  tick();
  r.indicatorTimer = setInterval(tick, 500);
  r.restoreTitle = () => { if (title) title.textContent = plain; };
}

// ---- start / stop -------------------------------------------------------------

const on = v => v != null && v !== false && String(v) !== "0";

export async function startCapture(opts = {}) {
  if (rec) { log("already recording: capture() again, or capture(stop), to stop", "info"); return; }
  const kind = on(opts.flac) ? "flac" : on(opts.wav) || on(opts.sound) ? "wav" : null;
  const bits = Number(opts.bits) === 24 ? 24 : 16;
  if (opts.bits != null && ![16, 24].includes(Number(opts.bits))) log(`bits: 16 or 24 -- ${bits} it is`, "info");
  if (on(opts.sound)) return startSoundOnly(opts, kind, bits);
  if (!navigator.mediaDevices?.getDisplayMedia || !window.MediaRecorder) {
    log("this browser cannot record the screen", "err"); return;
  }
  const path = cleanPath(opts.path ?? "recordings");
  if (!path) { log(`path: "${opts.path}" -- a folder in the project, like recordings/takes`, "err"); return; }
  const fmt = pickMime(String(opts.format ?? "mp4").toLowerCase());
  if (!fmt) { log("no video format this browser can record", "err"); return; }
  if (String(opts.format ?? "mp4").toLowerCase() === "mp4" && fmt.ext !== "mp4") log("mp4 is not recordable here: webm instead", "info");
  const fps = Math.max(1, Math.min(120, Number(opts.fps) || 30));

  // the size: the window's own, a height, or exact
  const dpr = window.devicePixelRatio || 1;
  const winW = Math.round(window.innerWidth * dpr), winH = Math.round(window.innerHeight * dpr);
  let W = winW, H = winH;
  if (Array.isArray(opts.size) && opts.size.length === 2) [W, H] = opts.size.map(Number);
  else if (Number(opts.size) > 0) { H = Number(opts.size); W = Math.round(H * winW / winH); }
  W -= W % 2; H -= H % 2;                       // encoders want even sizes

  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: fps, max: fps }, width: { ideal: W }, height: { ideal: H } },
      audio: false,
      preferCurrentTab: true, selfBrowserSurface: "include", surfaceSwitching: "exclude",
    });
  } catch (err) {
    log(`not recording: ${err.message || err}`, "err"); return;
  }
  const video = stream.getVideoTracks()[0];
  try { await video.applyConstraints({ width: W, height: H, frameRate: fps }); } catch { /* the size it gives */ }

  // the sound: Oscilla's own mix
  const ctx = masterMix.context;
  const dest = ctx.createMediaStreamDestination();
  masterMix.connect(dest);
  const mixed = new MediaStream([video, ...dest.stream.getAudioTracks()]);

  const base = `capture-${stamp()}`;
  const r = rec = {
    project: getProjectName(), path, base, fps, ext: fmt.ext,
    name: `${base}.${fmt.ext}`, stream, dest, chain: Promise.resolve(), bytes: 0,
    label: `${W}×${H} ${fps} fps ${fmt.ext}`,
  };

  if (kind) {
    try { r.wav = await startSound(r, ctx, kind, bits); r.label += ` + ${r.wav.kind} ${r.wav.channels} ch`; }
    catch (err) { log(`${kind}: ${err.message}`, "err"); }
  }

  const mr = r.recorder = new MediaRecorder(mixed, { mimeType: fmt.mime, videoBitsPerSecond: Math.round(W * H * fps * 0.12) });
  mr.ondataavailable = (e) => {
    if (!e.data?.size) return;
    r.bytes += e.data.size;
    r.chain = r.chain.then(() => send(r, r.name, e.data)).catch(err => log(`saving: ${err.message}`, "err"));
  };
  mr.onstop = () => finish(r);
  video.addEventListener("ended", () => { if (rec === r) stopCapture(); });   // sharing stopped
  mr.start(1000);
  showIndicator(r);
  log(`recording ${r.label} → ${path}/${r.name}`, "ok");
}

/** capture(sound): no picture, no screen to share -- the file alone. */
async function startSoundOnly(opts, kind, bits) {
  const path = cleanPath(opts.path ?? "recordings");
  if (!path) { log(`path: "${opts.path}" -- a folder in the project, like recordings/takes`, "err"); return; }
  const ctx = masterMix.context;
  const base = `capture-${stamp()}`;
  const r = rec = { project: getProjectName(), path, base, soundOnly: true, chain: Promise.resolve(), bytes: 0, label: "" };
  try { r.wav = await startSound(r, ctx, kind, bits); }
  catch (err) { rec = null; log(`${kind}: ${err.message}`, "err"); return; }
  r.name = r.wav.name;
  r.label = `${r.wav.kind} ${r.wav.channels} ch ${bits} bit`;
  showIndicator(r);
  log(`recording ${r.label} → ${path}/${r.name}`, "ok");
}

export function stopCapture() {
  const r = rec;
  if (!r) { log("not recording", "info"); return; }
  rec = null;
  if (r.soundOnly) { finish(r); return; }
  try { r.recorder.state !== "inactive" && r.recorder.stop(); } catch { finish(r); }
}

async function finish(r) {
  if (r.finished) return;
  r.finished = true;
  clearInterval(r.indicatorTimer);
  r.indicator?.remove();
  r.restoreTitle?.();
  for (const t of r.stream?.getTracks() || []) t.stop();
  if (r.dest) { try { masterMix.disconnect(r.dest); } catch { /* gone */ } }
  if (r.wav) await endSound(r, r.wav);
  if (r.soundOnly) {
    log(`saved ${r.path}/${r.name} (${(r.wav.bytes / 1048576).toFixed(1)} MB)`, "ok");
    return;
  }
  await r.chain;
  // the browser stamps the sound's packets unevenly: players then cut and
  // pad it. The server evens them out (server/mp4AudioTimes.js).
  if (r.ext === "mp4") await send(r, r.name, new Blob(), "finish").catch(err => log(`saving: ${err.message}`, "err"));
  const mb = (r.bytes / 1048576).toFixed(1);
  log(`saved ${r.path}/${r.name} (${mb} MB)${r.wav ? ` and ${r.wav.name}` : ""}`, "ok");
}

export const isCapturing = () => !!rec;

/** capture(...) as a cue. */
export function handleCaptureCue(ast) {
  // the projector window runs what the laptop runs: it does not record too
  if (new URLSearchParams(location.search).get("follow") === "1") return;
  const opts = {};
  for (const a of ast?.args || []) opts[a.type] = a.value;
  if ("stop" in opts || "off" in opts) return stopCapture();
  if (rec) return stopCapture();
  return startCapture(opts);
}
